import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, Container, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Collapse, Box
} from '@mui/material';
import useApi from '../Utils/useApi';
import useCoreApi from '../Utils/useCoreApi';

const AssessmentHistory = () => {
  const navigate = useNavigate();
  const { subjectId } = useParams();
  const api = useApi();
  const coreApi = useCoreApi();

  const [assessmentHistory, setAssessmentHistory] = useState([]);
  const [expandedResultId, setExpandedResultId] = useState(null);
  const [detailedAnswers, setDetailedAnswers] = useState({}); // { result_id: [...] }

  const userId = localStorage.getItem('user_id');

  useEffect(() => {
    let endpoint = `/assess/getAssessmentHistory/${userId}`;
    if (subjectId) endpoint += `?subject_id=${subjectId}`;

    api.get(endpoint)
      .then((response) => setAssessmentHistory(response.data))
      .catch((error) => console.error('Error fetching assessment history:', error));
  }, [subjectId]);

  const handleRetakeAssessment = (subjectId) => {
    navigate(`/assessment-questions/${subjectId}`);
  };

  const toggleDetails = async (resultId) => {
    if (expandedResultId === resultId) {
      setExpandedResultId(null);
      return;
    }

    if (!detailedAnswers[resultId]) {
      try {
        const res = await coreApi.get(`/assessment/answers/by-result/${resultId}`);
        setDetailedAnswers((prev) => ({ ...prev, [resultId]: res.data.answers }));
      } catch (err) {
        console.error('Error loading detailed answers:', err);
      }
    }

    setExpandedResultId(resultId);
  };

  const getSubjectName = (subjectId) => {
    const subject = SUBJECTS.find((s) => s.id === parseInt(subjectId));
    return subject ? subject.name : 'Unknown Subject';
  };

  return (
    <Container maxWidth="lg" style={{ marginTop: '20px' }}>
      <Typography variant="h4" gutterBottom>
        {subjectId ? `Assessment History for ${getSubjectName(subjectId)}` : 'Assessment History'}
      </Typography>

      {assessmentHistory.length === 0 ? (
        <>
          <Typography variant="body1">
            You have not taken any assessments {subjectId ? 'for this subject' : ''} yet.
          </Typography>
          <Button variant="contained" onClick={() => navigate(-1)} style={{ marginTop: '20px' }}>
            Back
          </Button>
        </>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table aria-label="assessment history table">
              <TableHead>
                <TableRow>
                  {!subjectId && <TableCell>Subject</TableCell>}
                  <TableCell align="right">Score</TableCell>
                  <TableCell align="right">Suggested Year Level</TableCell>
                  <TableCell align="right">Date Taken</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentHistory.map((assessment) => (
                  <React.Fragment key={assessment.id}>
                    <TableRow>
                      {!subjectId && (
                        <TableCell>{assessment.subject_name}</TableCell>
                      )}
                      <TableCell align="right">{assessment.score}</TableCell>
                      <TableCell align="right">{assessment.suggested_year_level}</TableCell>
                      <TableCell align="right">{assessment.date_taken}</TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" onClick={() => handleRetakeAssessment(assessment.subject_id)}>
                          Retake
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => toggleDetails(assessment.id)}
                        >
                          {expandedResultId === assessment.id ? 'Hide' : 'View'} Details
                        </Button>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={5} style={{ paddingBottom: 0, paddingTop: 0 }}>
                        <Collapse in={expandedResultId === assessment.id} timeout="auto" unmountOnExit>
                          <Box margin={2}>
                            <Typography variant="subtitle1" gutterBottom>
                              Answers:
                            </Typography>
                            {detailedAnswers[assessment.id] ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Question</TableCell>
                                    <TableCell>Your Answer</TableCell>
                                    <TableCell>Correct Answer</TableCell>
                                    <TableCell>Correct?</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {detailedAnswers[assessment.id].map((answer, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{answer.question_text}</TableCell>
                                      <TableCell>{answer.selected_answer}</TableCell>
                                      <TableCell>{answer.correct_answer}</TableCell>
                                      <TableCell>{answer.is_correct ? '✅' : '❌'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2">Loading...</Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button variant="contained" color="primary" onClick={() => navigate(-1)} style={{ marginTop: '20px' }}>
            Back
          </Button>
        </>
      )}
    </Container>
  );
};

const SUBJECTS = [
  { id: 1, name: 'Maths' },
  { id: 2, name: 'Science' },
  { id: 3, name: 'Geography' },
  { id: 4, name: 'History' },
  { id: 5, name: 'English' },
  { id: 6, name: 'ICT' },
  { id: 7, name: 'Business Studies' },
];

export default AssessmentHistory;
